import { useDisclosure } from '@doda/common/src/hooks/useDisclosure';
import { saveSearchConditions } from '@doda/common/src/services/saveSearchConditions';
import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { DODA_SITE_URL } from '@doda/common/src/constants/urls';
import {
  createSearchConditionName,
  makeSendConditionParam,
} from '@doda/common/src/logics/saveCondition/saveCondition';
import { convertSavedSearchConditionsToQueryParams } from 'logics/selectOverwriteCondition';
import { TEMPLATE_CONDITION } from '@doda/common/src/constants/searchConditionModal';
import { postSearchConditions } from '@doda/common/src/services/conditionRegistAPI';
import { SelectedCurrentConditionType } from 'types/searchCondition';
import { QUERY_PARAMS } from '@doda/common/src/constants/queryParams';
import { formatString } from '@doda/common/src/utils/stringUtil';
import { createSaveConditionsRequest } from 'logics/createSaveConditionsRequest';
import { useAppSelector } from '@doda/pc/src/redux/hooks';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { JOB_SEARCH_LIST_LINK, MAX_SAVED_COUNT } from '../../constants';

/**
 * moveToLoginPage Props
 */
interface ArgumentType {
  /** ログイン状態 */
  isLogin: boolean;
  /** 検索条件のラベル */
  searchConditionLabel: string | undefined;
  /** 保存された検索条件の数 */
  savedSearchCount: number;
  /** 現在選択されている検索条件 */
  selectedCurrentCondition: SelectedCurrentConditionType;
}

/**
 * ログイン画面へ遷移し、ログイン後保存した検索条件一覧画面へ遷移する
 *
 * @param {SelectedCurrentConditionType} selectedCurrentCondition - 現在選択されている検索条件
 * @param {boolean} [isJobSearchList] - ジョブ検索リストかどうかのフラグ
 * @param {boolean} [isLogin] - ログイン状態
 */
const moveToLoginPage = (
  selectedCurrentCondition: SelectedCurrentConditionType,
  isJobSearchList?: boolean,
  isLogin?: boolean
) => {
  const conditions = createSaveConditionsRequest(selectedCurrentCondition);
  const url = '/dcfront/login/login/';
  const searchConditionName = createSearchConditionName();
  const param = {
    oldestDayWdtno: 0,
    SP_CC_wsernm: searchConditionName,
  };
  // 検索条件保存リクエストパラメータから対応するクエリパラメータに変換
  const newConditions = convertSavedSearchConditionsToQueryParams(conditions);
  const sendCondition = makeSendConditionParam(newConditions);
  const newParam = {
    tp: '1',
    ...TEMPLATE_CONDITION,
    ...param,
    ...sendCondition,
  };
  postSearchConditions(
    url,
    newParam,
    isJobSearchList
      ? {
          [QUERY_PARAMS.usrclk_searchList]: formatString(
            JOB_SEARCH_LIST_LINK.SAVED_SEARCH_BUTTON_USRCLK,
            [isLogin ? 'login' : 'logout']
          ),
        }
      : {}
  );
};

/**
 * サイドバー用現在の検索条件カスタムフック
 *
 * @param {ArgumentType} - フックの引数
 * @returns {Object} 各種状態とイベントハンドラを持つオブジェクト
 */
export const useCurrentCondition = ({
  isLogin,
  searchConditionLabel,
  savedSearchCount,
  selectedCurrentCondition,
}: ArgumentType) => {
  const detailModal = useDisclosure();
  const overWriteModal = useDisclosure();
  const isJobSearchList =
    useRouter().pathname === DODA_SITE_URL.jobSearchList.replace('.action', '');

  const isMaxSavedCount = savedSearchCount === MAX_SAVED_COUNT;

  const { nearArea } = useAppSelector(appQueryParamsSelector);
  const isNearAreaPage = !!nearArea;

  const isShowSaveButton =
    searchConditionLabel !== undefined && !isNearAreaPage;

  // 検索結果一覧用
  const onClickSaveButtonJobSearchList = useCallback(async () => {
    if (!isLogin) {
      moveToLoginPage(selectedCurrentCondition, isJobSearchList, isLogin);
    } else if (isMaxSavedCount) {
      overWriteModal.onOpen();
    } else {
      // 保存処理
      await saveSearchConditions(
        createSaveConditionsRequest(selectedCurrentCondition)
      );
      const mpscDtno = savedSearchCount + 1;
      window.location.href = `${DODA_SITE_URL.recommendJobList}/?mpsc_dtno=${mpscDtno}&usrclk_searchList=${formatString(JOB_SEARCH_LIST_LINK.SAVED_SEARCH_BUTTON_USRCLK, [isLogin ? 'login' : 'logout'])}`;
    }
  }, [
    isJobSearchList,
    isLogin,
    isMaxSavedCount,
    overWriteModal,
    savedSearchCount,
    selectedCurrentCondition,
  ]);

  // 希望条件にマッチした求人一覧用
  const onClickSaveButtonMatchJobList = useCallback(async () => {
    if (!isLogin) {
      moveToLoginPage(selectedCurrentCondition);
    } else if (isMaxSavedCount) {
      overWriteModal.onOpen();
    } else {
      // 保存処理
      await saveSearchConditions(
        createSaveConditionsRequest(selectedCurrentCondition)
      );

      const mpscDtno = savedSearchCount + 1;
      window.location.href = `${DODA_SITE_URL.recommendJobList}/?mpsc_dtno=${mpscDtno}`;
    }
  }, [
    isLogin,
    isMaxSavedCount,
    overWriteModal,
    savedSearchCount,
    selectedCurrentCondition,
  ]);

  return {
    isShowSaveButton,
    onClickSaveButtonJobSearchList,
    onClickSaveButtonMatchJobList,
    overWriteModal,
    detailModal,
  };
};
